import type { ArticleContent, ContentBlock } from '@/lib/articleContent';
import type { CmsArticle, SiteStaffMember, SiteStaffRole, SiteStaffXatRank } from '@/lib/cms-types';
import type { PublicGuideListItem } from '@/lib/cms-guides';
import {
  MOCK_PAINEL_USERS,
  type PainelMockUser,
} from '@/lib/painel-rbac';
import { getPainelUserInitials } from '@/lib/painel-activity';
import {
  STAFF_XAT_RANK_OPTIONS,
  STAFF_XAT_RANK_PRESETS,
  resolveStaffXatRank,
} from '@/lib/equipe-xat-ranks';
import { findStaticGuideAuthorMock } from '@/lib/static-guide-author-mocks';
import { resolveMediaUrl } from '@/services/api-client';

export type ArticleAuthor = {
  name: string;
  /** URL da foto; omitir para avatar com iniciais. */
  avatarUrl?: string;
  avatarInitials: string;
  /** Cargo na equipe xat Ajuda (Main Owner, Moderador, …). */
  helpCenterRoleLabel?: string;
  /** Rank oficial xat com cor (Volunteer, Wiki Editor, …). */
  officialRank?: SiteStaffXatRank | null;
};

export type ArticleAuthorCardProps = {
  author: ArticleAuthor;
  publishedAt?: string | null;
  updatedAt?: string | null;
  readingTimeMinutes: number;
  views?: number;
  className?: string;
};

const DEFAULT_AUTHOR: ArticleAuthor = {
  name: 'Equipe xat',
  avatarInitials: 'XA',
  helpCenterRoleLabel: 'Suporte Oficial',
  officialRank: null,
};

export const HELP_CENTER_ROLE_LABEL: Record<SiteStaffRole, string> = {
  mainOwner: 'Main Owner',
  owners: 'Owner',
  moderadores: 'Moderador',
  membros: 'Membro',
};

/** Fallback por username quando a ficha em /equipe ainda não está no CMS. */
const PAINEL_USERNAME_HELP_CENTER_ROLE: Record<string, SiteStaffRole> = {
  cupim: 'mainOwner',
  guinho: 'mainOwner',
  thuk: 'mainOwner',
  asta: 'mainOwner',
};

type AuthorResolveContext = {
  painelUsers?: PainelMockUser[];
  staffMembers?: SiteStaffMember[];
};

const READING_WPM = 200;

function extractTextFromBlock(block: ContentBlock): string {
  switch (block.type) {
    case 'paragraph':
    case 'heading':
      return block.text;
    case 'steps':
      return block.items.map((s) => `${s.title} ${s.description}`).join(' ');
    case 'list':
      return block.items.join(' ');
    case 'info':
    case 'tip':
    case 'warning':
      return `${block.title} ${block.content}`;
    case 'table':
      return [
        block.headers.join(' '),
        ...block.rows.map((row) => row.join(' ')),
      ].join(' ');
    case 'code':
      return block.code;
    case 'divider':
      return '';
    default:
      return '';
  }
}

export function estimateReadingTimeMinutes(body?: ArticleContent): number {
  if (!body) return 1;
  const chunks = [body.intro, ...body.blocks.map(extractTextFromBlock)];
  const text = chunks.join(' ').replace(/<[^>]+>/g, ' ');
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  if (words === 0) return 1;
  return Math.max(1, Math.round(words / READING_WPM));
}

const MONTHS_PT = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
] as const;

export function formatArticleDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const day = date.getDate();
  const month = MONTHS_PT[date.getMonth()] ?? '???';
  const year = date.getFullYear();
  const time = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  return `${day} ${month} ${year} • ${time}`;
}

function findStaffMemberForUser(
  user: PainelMockUser,
  staffMembers?: SiteStaffMember[]
): SiteStaffMember | undefined {
  if (!staffMembers?.length) return undefined;
  const username = user.username.trim().toLowerCase();
  const nameKey = user.name.trim().toLowerCase();
  return staffMembers.find((member) => {
    const memberUsername = member.username.trim().toLowerCase();
    const memberName = member.name.trim().toLowerCase();
    return (
      memberUsername === username ||
      memberName === nameKey ||
      memberUsername === nameKey
    );
  });
}

function resolveHelpCenterStaffRole(
  user: PainelMockUser,
  staffMembers?: SiteStaffMember[]
): SiteStaffRole | null {
  if (user.helpCenterRole !== undefined) {
    return user.helpCenterRole;
  }

  const fromStaff = findStaffMemberForUser(user, staffMembers);
  if (fromStaff) return fromStaff.role;

  const username = user.username.trim().toLowerCase();
  const nameKey = user.name.trim().toLowerCase();
  return (
    PAINEL_USERNAME_HELP_CENTER_ROLE[username] ??
    PAINEL_USERNAME_HELP_CENTER_ROLE[nameKey] ??
    null
  );
}

function officialXatLabelToRank(label: string | null | undefined): SiteStaffXatRank | null {
  if (!label) return null;
  const match = STAFF_XAT_RANK_OPTIONS.find((o) => o.rank.label === label);
  return match?.rank ?? null;
}

function resolveOfficialXatRank(
  user: PainelMockUser,
  staffMembers?: SiteStaffMember[]
): SiteStaffXatRank | null {
  const fromStaff = findStaffMemberForUser(user, staffMembers);
  if (fromStaff) return resolveStaffXatRank(fromStaff);

  if (user.officialXatRole === null) return null;

  if (user.officialXatRole) {
    const match = STAFF_XAT_RANK_OPTIONS.find((o) => o.rank.label === user.officialXatRole);
    return match?.rank ?? null;
  }

  const preset = STAFF_XAT_RANK_PRESETS[user.username.trim().toLowerCase()];
  return preset ?? null;
}

function resolveAuthorFromPainelUser(
  user: PainelMockUser,
  ctx: AuthorResolveContext = {}
): ArticleAuthor {
  const staffRole = resolveHelpCenterStaffRole(user, ctx.staffMembers);
  const avatarUrl = user.avatarUrl ? resolveMediaUrl(user.avatarUrl) ?? user.avatarUrl : undefined;
  return {
    name: user.name,
    avatarUrl,
    avatarInitials: getPainelUserInitials(user.name),
    helpCenterRoleLabel: staffRole ? HELP_CENTER_ROLE_LABEL[staffRole] : undefined,
    officialRank: resolveOfficialXatRank(user, ctx.staffMembers),
  };
}

function findPainelUser(
  userId: string | undefined,
  users: PainelMockUser[]
): PainelMockUser | undefined {
  if (!userId) return undefined;
  return users.find((u) => u.id === userId);
}

function findPainelUserByName(name: string, users: PainelMockUser[]): PainelMockUser | undefined {
  const key = name.trim().toLowerCase();
  return users.find((u) => u.name.trim().toLowerCase() === key);
}

function withGuideUpdaterAvatar(
  author: ArticleAuthor,
  article: Pick<CmsArticle, 'updatedByAvatarUrl'>
): ArticleAuthor {
  const fromGuide = resolveMediaUrl(article.updatedByAvatarUrl);
  if (fromGuide) return { ...author, avatarUrl: fromGuide };
  return author;
}

export function resolveAuthorFromCmsArticle(
  article: Pick<
    CmsArticle,
    | 'updatedById'
    | 'updatedByName'
    | 'updatedByHelpCenterRole'
    | 'updatedByOfficialXatRole'
    | 'updatedByAvatarUrl'
  >,
  ctx: AuthorResolveContext = {}
): ArticleAuthor {
  const users = ctx.painelUsers ?? MOCK_PAINEL_USERS;
  const byId = findPainelUser(article.updatedById, users);
  if (byId) {
    return withGuideUpdaterAvatar(resolveAuthorFromPainelUser(byId, ctx), article);
  }

  if (article.updatedByName?.trim()) {
    const byName = findPainelUserByName(article.updatedByName, users);
    if (byName) {
      return withGuideUpdaterAvatar(resolveAuthorFromPainelUser(byName, ctx), article);
    }

    const helpRole = article.updatedByHelpCenterRole;
    const avatarFromApi = resolveMediaUrl(article.updatedByAvatarUrl);
    return {
      name: article.updatedByName.trim(),
      avatarUrl: avatarFromApi ?? undefined,
      avatarInitials: getPainelUserInitials(article.updatedByName),
      helpCenterRoleLabel: helpRole ? HELP_CENTER_ROLE_LABEL[helpRole] : undefined,
      officialRank: officialXatLabelToRank(article.updatedByOfficialXatRole),
    };
  }

  return DEFAULT_AUTHOR;
}

function shouldShowUpdatedAt(publishedAt: string, updatedAt: string): boolean {
  const pub = new Date(publishedAt).getTime();
  const upd = new Date(updatedAt).getTime();
  if (Number.isNaN(pub) || Number.isNaN(upd)) return false;
  return Math.abs(upd - pub) > 60_000;
}

export function resolveArticleAuthorCardProps(input: {
  cmsArticle?: CmsArticle;
  body?: ArticleContent;
  painelUsers?: PainelMockUser[];
  staffMembers?: SiteStaffMember[];
}): ArticleAuthorCardProps {
  const ctx: AuthorResolveContext = {
    painelUsers: input.painelUsers,
    staffMembers: input.staffMembers,
  };
  const readingTimeMinutes = estimateReadingTimeMinutes(input.body);

  if (input.cmsArticle) {
    const author = resolveAuthorFromCmsArticle(input.cmsArticle, ctx);
    const publishedAt = input.cmsArticle.publishedAt ?? input.cmsArticle.createdAt;
    const updatedAt = shouldShowUpdatedAt(publishedAt, input.cmsArticle.updatedAt)
      ? input.cmsArticle.updatedAt
      : null;
    const readingTimeMinutes =
      input.cmsArticle.readingTime && input.cmsArticle.readingTime > 0
        ? input.cmsArticle.readingTime
        : estimateReadingTimeMinutes(input.body);
    return {
      author,
      publishedAt,
      updatedAt,
      readingTimeMinutes,
      views: input.cmsArticle.views ?? 0,
    };
  }

  return {
    author: DEFAULT_AUTHOR,
    publishedAt: null,
    updatedAt: null,
    readingTimeMinutes,
    views: 0,
  };
}

export function findCmsArticleForPublicGuide(
  categoryId: string,
  item: PublicGuideListItem,
  cmsArticles: CmsArticle[]
): CmsArticle | undefined {
  if (item.cmsId) {
    const row = cmsArticles.find((a) => a.id === item.cmsId);
    return row?.status === 'published' ? row : undefined;
  }
  return cmsArticles.find(
    (a) =>
      a.categoryId === categoryId &&
      a.status === 'published' &&
      (a.slug?.toLowerCase() === item.slug || a.id === item.key.replace('cms-', ''))
  );
}

export function resolveArticleAuthorCardForGuide(input: {
  categoryId: string;
  item: PublicGuideListItem;
  cmsArticles: CmsArticle[];
  body?: ArticleContent;
  painelUsers?: PainelMockUser[];
  staffMembers?: SiteStaffMember[];
}): ArticleAuthorCardProps {
  const users = input.painelUsers ?? MOCK_PAINEL_USERS;
  const ctx: AuthorResolveContext = {
    painelUsers: users,
    staffMembers: input.staffMembers,
  };
  const readingTimeMinutes = estimateReadingTimeMinutes(input.body);

  const cmsArticle = findCmsArticleForPublicGuide(
    input.categoryId,
    input.item,
    input.cmsArticles
  );

  const staticMock = cmsArticle?.sourceRef
    ? (() => {
        const [, staticIdStr] = cmsArticle.sourceRef.split(':');
        const staticId = Number(staticIdStr);
        return Number.isFinite(staticId)
          ? findStaticGuideAuthorMock(input.categoryId, staticId)
          : undefined;
      })()
    : undefined;

  if (cmsArticle) {
    const hasCmsAuthor =
      Boolean(cmsArticle.updatedById) || Boolean(cmsArticle.updatedByName?.trim());

    if (hasCmsAuthor) {
      return resolveArticleAuthorCardProps({
        cmsArticle,
        body: input.body,
        painelUsers: users,
        staffMembers: input.staffMembers,
      });
    }

    if (staticMock) {
      const user = findPainelUser(staticMock.authorUserId, users);
      const author = user ? resolveAuthorFromPainelUser(user, ctx) : DEFAULT_AUTHOR;
      const publishedAt = cmsArticle.publishedAt ?? cmsArticle.createdAt;
      const updatedAt = shouldShowUpdatedAt(publishedAt, cmsArticle.updatedAt)
        ? cmsArticle.updatedAt
        : null;
      return {
        author,
        publishedAt,
        updatedAt,
        readingTimeMinutes:
          cmsArticle.readingTime && cmsArticle.readingTime > 0
            ? cmsArticle.readingTime
            : readingTimeMinutes,
        views: cmsArticle.views ?? 0,
      };
    }

    return resolveArticleAuthorCardProps({
      cmsArticle,
      body: input.body,
      painelUsers: users,
      staffMembers: input.staffMembers,
    });
  }

  if (staticMock) {
    const user = findPainelUser(staticMock.authorUserId, users);
    const author = user ? resolveAuthorFromPainelUser(user, ctx) : DEFAULT_AUTHOR;
    const updatedAt =
      staticMock.updatedAt &&
      shouldShowUpdatedAt(staticMock.publishedAt, staticMock.updatedAt)
        ? staticMock.updatedAt
        : null;
    return {
      author,
      publishedAt: staticMock.publishedAt,
      updatedAt,
      readingTimeMinutes,
      views: input.item.views ?? 0,
    };
  }

  return {
    author: DEFAULT_AUTHOR,
    publishedAt: null,
    updatedAt: null,
    readingTimeMinutes,
    views: input.item.views ?? 0,
  };
}
